import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { InfinitePaginatedList, PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  ContentLoader,
  Heading,
  SearchField,
  Text,
  useDebouncedValue,
  useFormField,
} from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { CreateListView } from '../types';
import { FilterButton } from './filter-button';
import { RecipientItem } from './recipient-item';
import { RecipientsViewFooter } from './recipients-view-footer';
import { RecipientsViewHeader } from './recipients-view-header';
import { useRecipientsView } from './use-recipients-view';
import { useSplitSelectionList } from './use-split-selection-list';

type Props = {
  startCreateListFrom: (fromView: CreateListView) => void;
};

export const RecipientsView = ({ startCreateListFrom }: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const { campaignType, filters, listToUpdate, mode, selectedRecipients, setMode, trackingIdPrefix } =
    useAudienceTray();
  const isTextCampaign = campaignType === CampaignType.SMS;

  // TODO: Consider consolidating these two lines into a useDebouncedSearch similar to what's happening in lists. Also consider moving them into the useRecipientsView hook.
  const searchFieldProps = useFormField({ type: 'text' });
  const searchQuery = useDebouncedValue(searchFieldProps.value, 800);

  const {
    abortSelectAllRecipients,
    deselectAllRecipients,
    deselectRecipient,
    infiniteQueryProps,
    isSelectAllLoading,
    selectAllRecipients,
    selectRecipient,
  } = useRecipientsView(searchQuery);

  const {
    bodyRef,
    headerRef,
    selectedContactsBinHeight,
    showSelectedBin,
    showSelectedBinPreference,
    showSelectedBinToggle,
    toggleSelectedUsersBin,
    unselectedContactsBinHeight,
  } = useSplitSelectionList(selectedRecipients.length);
  // TODO: Add empty state for when there are no recipients to pick from
  // TODO: Rework, improve, and move the code here into a new split selection component. Replace other instances in the app with it.

  const searchFieldPlaceholder = isTextCampaign ? t('Search Name, Phone, or ID') : t('Search Name, Email, or ID');

  return (
    <>
      <ContentLoader show={isSelectAllLoading} message={t('Selecting All...')} css={{ position: 'fixed' }}>
        <Button
          size='large'
          variant='secondary'
          css={{ marginTop: theme.spacing(4) }}
          onClick={abortSelectAllRecipients}
        >
          {t('Cancel')}
        </Button>
      </ContentLoader>
      {mode === 'recipients-edit-list' && listToUpdate?.name && (
        <Heading level={2} css={{ margin: theme.spacing(2, 4, 1, 4) }}>
          {listToUpdate.name}
        </Heading>
      )}
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: theme.spacing(1),
          margin: theme.spacing(2, 4, 0, 4),
        }}
      >
        <SearchField
          {...searchFieldProps}
          name='recipient-search'
          placeholder={searchFieldPlaceholder}
          aria-label={searchFieldPlaceholder}
          maxLength={70}
          data-trackingid={`${trackingIdPrefix}-search-input`}
        />
        {/* TODO: Add another mode to handle applying a filter while creating or editing a list */}
        <FilterButton active={!!Object.keys(filters).length} onClick={() => setMode('filters')} />
      </div>
      <Button
        variant='tertiary'
        size='small'
        onClick={selectAllRecipients}
        trackingId={`${trackingIdPrefix}-select-all-button`}
        css={{ margin: theme.spacing(1, 1, 1, 4) }}
      >
        {t('Select All')}
      </Button>
      <div
        ref={bodyRef}
        css={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          gap: theme.spacing(2),
          height: mode === 'recipients-edit-list' ? 'calc(100% - 205px)' : 'calc(100% - 160px)', // TODO: Make this dynamic based on the footer height
          margin: theme.spacing(0, 4),
        }}
      >
        <InfinitePaginatedList
          height={unselectedContactsBinHeight}
          infiniteQueryProps={infiniteQueryProps}
          endOfListText=''
          renderListItem={({ listItem: recipient }) => (
            <RecipientItem
              checked={false}
              isTextCampaign={isTextCampaign}
              key={recipient.destination}
              onClick={() => selectRecipient(recipient)}
              recipient={recipient}
              search={searchQuery}
            />
          )}
        />
        {showSelectedBin && (
          <div
            css={{
              border: `solid 1px ${theme.colors.neutral20}`,
              borderRadius: theme.borderRadius.small,
              flexGrow: 1,
              overflowY: 'auto',
            }}
          >
            <div
              ref={headerRef}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: theme.spacing(1),
                borderBottom: `solid 1px ${theme.colors.neutral20}`,
              }}
            >
              <Button size='small' variant='tertiary' onClick={deselectAllRecipients}>
                {t('Deselect All')}
              </Button>
              {mode !== 'recipients-select-list' && mode !== 'recipients-edit-list' && (
                <Button variant='tertiary' size='small' onClick={() => startCreateListFrom('recipients')}>
                  {t('Save List')}
                </Button>
              )}
            </div>
            <PaginatedList
              listData={selectedRecipients}
              height={selectedContactsBinHeight}
              endOfListText=''
              renderListItem={({ listItem: recipient }) => (
                <RecipientItem
                  checked={true}
                  isTextCampaign={isTextCampaign}
                  key={recipient.destination}
                  onClick={deselectRecipient}
                  recipient={recipient}
                  search={searchQuery}
                />
              )}
            />
          </div>
        )}
      </div>
      <section>
        <div
          css={{
            alignItems: 'start',
            display: 'flex',
            justifyContent: showSelectedBinToggle ? 'space-between' : 'flex-end',
            padding: theme.spacing(1, 4, 0, 4),
            borderTop:
              showSelectedBinToggle && showSelectedBinPreference
                ? `1px solid transparent`
                : `1px solid ${theme.colors.neutral20}`,
          }}
        >
          {showSelectedBinToggle && (
            <Button size='small' variant='tertiary' onClick={toggleSelectedUsersBin}>
              {showSelectedBinPreference ? t('Hide selected recipients') : t('Show selected recipients')}
            </Button>
          )}
          <Text color={'subdued'} css={{ fontSize: theme.fontSize(24) }} weight='bold'>
            {t('{{count}} Contact', { count: selectedRecipients.length })}
          </Text>
        </div>
      </section>
    </>
  );
};

RecipientsView.Header = RecipientsViewHeader;
RecipientsView.Footer = RecipientsViewFooter;
